import React, { useRef } from 'react';
import ReactECharts from 'echarts-for-react';

const CO2ComparisonBarChart = ({ design1CO2eq_median, design2CO2eq_median, analysisPeriod, design1title, design2title }) => {
    const chartRef = useRef(null);
    
    if (!design1CO2eq_median.length || !design2CO2eq_median.length) {
        return <div>Loading...</div>;
    }

    const data1 = design1CO2eq_median;
    const data2 = design2CO2eq_median;

    const co2eq1Final = data1[data1.length - 1].map(val => val / 1e6);
    const co2eq2Final = data2[data2.length - 1].map(val => val / 1e6);

    const total1 = co2eq1Final.reduce((acc, val) => acc + val, 0);
    const total2 = co2eq2Final.reduce((acc, val) => acc + val, 0);

    const xLabels = [design1title, design2title];

    const colors = [
        '#0072B2',  // Initial
        '#4DBEEE',  // M&R
        '#F5A28F',  // PVI Deflection
        '#E55532',  // PVI Roughness
        '#AA8FCA',  // Radiative Forcing
        '#692760',  // Carbonation
        '#9E9E9E'   // EOL
    ];

    const legend = ['Initial', 'M&R', 'PVI Deflection', 'PVI Roughness', 'Radiative Forcing', 'Carbonation', 'End of Life'];

    const calculateTotals = (selectedSeries) => {
        let newTotal1 = 0;
        let newTotal2 = 0;
        
        legend.forEach((name, index) => {
            if (selectedSeries[name] !== false) {
                newTotal1 += co2eq1Final[index] || 0;
                newTotal2 += co2eq2Final[index] || 0;
            }
        });
        
        return { newTotal1, newTotal2 };
    };

    const option = {
        title: {
            text: 'Life Cycle GHG Emission',
            left: 'center'
        },
        tooltip: {
            trigger: 'item',
            formatter: function (params) {
                const value = Math.round(params.value * 1000) / 1000;
                return `${params.name}<br/>${params.seriesName}: ${value} kt`;
            }
        },
        legend: {
            data: legend,
            bottom: '0%',
            left: 'center',
        },
        xAxis: {
            type: 'category',
            data: xLabels,
            axisLabel: {
                fontSize: 14
            }
        },
        yAxis: {
            type: 'value',
            axisLine: {
                show: true,
            },
            axisTick: {
                show: true,
            },
            axisLabel: {
                formatter: '{value}',
                fontSize: 14
            },
            name: `Life Cycle GHG Emission (kt (CO2-eq))`,
            nameLocation: 'middle',
            nameGap: 30,
            nameTextStyle: {
                fontSize: 14,
            },
        },
        
        series: [
            // Regular data series
            ...legend.map((name, index) => ({
                name,
                type: 'bar',
                stack: 'total',
                data: [co2eq1Final[index], co2eq2Final[index]],
                itemStyle: {
                    color: colors[index]
                }
            })),
            // Invisible series just for the markPoints - this ensures dots always show
            {
                name: 'Totals', // Don't include this in legend
                type: 'scatter', // Use scatter so it doesn't affect the bars
                data: [
                    [0, total1], // Position at category 0 (first bar) with height of total1
                    [1, total2]  // Position at category 1 (second bar) with height of total2
                ],
                itemStyle: {
                    opacity: 0 // Make the scatter points invisible
                },
                symbolSize: 0, // Make sure scatter points are invisible
                markPoint: {
                    data: [
                        {
                            name: 'Design 1 Total',
                            coord: [0, total1],
                            value: Math.round(total1 * 100) / 100,
                            label: {
                                formatter: '{c} kt',
                                position: 'top',
                                fontSize: 16,
                                offset: [0, -5],
                                fontWeight: 'bold'
                            },
                            symbol: 'circle',
                            symbolSize: 10,
                            itemStyle: {
                                color: '#fff',
                                borderColor: '#000',
                                borderWidth: 2
                            }
                        },
                        {
                            name: 'Design 2 Total',
                            coord: [1, total2],
                            value: Math.round(total2 * 100) / 100,
                            label: {
                                formatter: '{c} kt',
                                position: 'top',
                                fontSize: 16,
                                offset: [0, -5],
                                fontWeight: 'bold'
                            },
                            symbol: 'circle',
                            symbolSize: 10,
                            itemStyle: {
                                color: '#fff',
                                borderColor: '#000',
                                borderWidth: 2
                            }
                        }
                    ]
                }
            }
        ]
    };

    const handleLegendSelectChanged = (params) => {
        const chart = chartRef.current?.getEchartsInstance();
        if (!chart) return;

        const { newTotal1, newTotal2 } = calculateTotals(params.selected);

        // Update the invisible series with new markPoint positions
        chart.setOption({
            series: [
                ...Array(legend.length).fill({}), // Empty objects for the bar series
                {
                    // Update the invisible scatter series
                    data: [
                        [0, newTotal1],
                        [1, newTotal2]
                    ],
                    markPoint: {
                        data: [
                            {
                                name: 'Design 1 Total',
                                coord: [0, newTotal1], // Move to new height
                                value: Math.round(newTotal1 * 100) / 100,
                                label: {
                                    formatter: '{c} kt',
                                    position: 'top',
                                    fontSize: 16,
                                    offset: [0, -5],
                                    fontWeight: 'bold'
                                },
                                symbol: 'circle',
                                symbolSize: 10,
                                itemStyle: {
                                    color: '#fff',
                                    borderColor: '#000',
                                    borderWidth: 2
                                }
                            },
                            {
                                name: 'Design 2 Total',
                                coord: [1, newTotal2], // Move to new height
                                value: Math.round(newTotal2 * 100) / 100,
                                label: {
                                    formatter: '{c} kt',
                                    position: 'top',
                                    fontSize: 16,
                                    offset: [0, -5],
                                    fontWeight: 'bold'
                                },
                                symbol: 'circle',
                                symbolSize: 10,
                                itemStyle: {
                                    color: '#fff',
                                    borderColor: '#000',
                                    borderWidth: 2
                                }
                            }
                        ]
                    }
                }
            ]
        });
    };

    return (
        <ReactECharts 
            ref={chartRef}
            option={option} 
            style={{ height: '500px', width: '100%' }}
            onEvents={{
                'legendselectchanged': handleLegendSelectChanged
            }}
        />
    );
};

export default CO2ComparisonBarChart;